import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BookOpen, Menu, X, ExternalLink } from "lucide-react";
import SearchBar from "@/components/SearchBar";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/categories", label: "Categories" },
  { to: "/services", label: "Services" },
  { to: "/companies", label: "Platforms" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const isActive = (to) =>
    to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-40 border-b border-[var(--line)] bg-white">
      <div className="top-bar-accent h-1 w-full" />
      <div className="mx-auto flex max-w-[1240px] items-center gap-4 px-4 py-3 lg:px-6">
        <Link to="/" className="flex items-center gap-2.5 no-underline" data-testid="site-logo">
          <span className="flex h-9 w-9 items-center justify-center rounded-[5px] bg-[var(--navy)] text-white">
            <BookOpen size={18} />
          </span>
          <span className="leading-tight">
            <span className="block text-[15px] font-bold text-[var(--navy)]">CompuBrain</span>
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)]">
              Knowledge Base
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex" data-testid="main-nav">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`rounded px-3 py-2 text-sm font-semibold no-underline transition-colors ${
                isActive(n.to)
                  ? "bg-[var(--bg-panel)] text-[var(--navy)]"
                  : "text-[var(--ink-soft)] hover:bg-[var(--bg-alt)] hover:text-[var(--navy)]"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto hidden w-[320px] md:block">
          <SearchBar variant="header" />
        </div>

        <button
          className="ml-auto lg:hidden"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-[var(--line)] bg-white px-4 py-3 lg:hidden" data-testid="mobile-menu">
          <div className="mb-3 md:hidden">
            <SearchBar variant="header" />
          </div>
          <div className="flex flex-col">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="rounded px-3 py-2 text-sm font-semibold text-[var(--ink-soft)] no-underline hover:bg-[var(--bg-alt)]"
              >
                {n.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-16 border-t border-[var(--line)] bg-[var(--navy)] text-white" data-testid="site-footer">
      <div className="mx-auto max-w-[1240px] px-4 py-10 lg:px-6">
        <div className="grid gap-8 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="mb-2 flex items-center gap-2">
              <BookOpen size={18} />
              <span className="text-lg font-bold">CompuBrain Knowledge Base</span>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-white/75">
              An interconnected knowledge base of digital services, categories and technology
              platforms. Explore how services, categories and platforms relate — and how
              CompuBrain delivers them.
            </p>
            <a
              href="https://compubrain.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--gold)] hover:underline"
              data-testid="footer-compubrain-link"
            >
              Visit CompuBrain <ExternalLink size={14} />
            </a>
          </div>
          <div>
            <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-white/90">Explore</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/categories" className="text-white/75 hover:text-white">Categories</Link></li>
              <li><Link to="/services" className="text-white/75 hover:text-white">Services</Link></li>
              <li><Link to="/companies" className="text-white/75 hover:text-white">Platforms</Link></li>
              <li><Link to="/search?q=ai" className="text-white/75 hover:text-white">Search</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-white/90">Resources</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="https://compubrain.com/knowledge/sitemap.xml" className="text-white/75 hover:text-white">XML Sitemap</a></li>
              <li><a href="https://compubrain.com/knowledge/robots.txt" className="text-white/75 hover:text-white">Robots.txt</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-8 border-t border-white/15 pt-5 text-xs text-white/60">
          © {new Date().getFullYear()} CompuBrain. Knowledge base for informational and discovery
          purposes. CompuBrain is the parent company providing the services and solutions described.
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
